import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useProduct } from "@/context/ProductContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, ArrowsClockwise, FloppyDisk, WarningCircle, CheckCircle, BookOpen, MapPin, Globe, Files } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NE = "Not estimable";

const APPROVAL_TONE = {
  approved_for_external_use: "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-300",
  changes_requested: "bg-amber-50 text-amber-700 border-amber-300",
  rejected: "bg-red-50 text-red-700 border-red-200",
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
};
const APPROVAL_LABEL = {
  approved_for_external_use: "Approved for external use",
  in_progress: "Review in progress",
  changes_requested: "Changes requested",
  rejected: "Rejected",
  not_started: "Review not started",
};

function NEBadge({ v }) {
  if (v === NE) return <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">{NE}</span>;
  return <span>{String(v)}</span>;
}

function Block({ b }) {
  if (b.type === "paragraph")  return <p className="text-xs text-slate-700 leading-relaxed">{b.text}</p>;
  if (b.type === "subheading") return <h4 className="font-heading font-bold text-sm mt-2">{b.text}</h4>;
  if (b.type === "bullet")     return <li className="text-xs text-slate-700 ml-4 list-disc">{b.text}</li>;
  if (b.type === "empty")      return <p className="text-xs text-amber-700 italic">{b.text}</p>;
  if (b.type === "kv") return (
    <table className="text-xs border-collapse w-full">
      <tbody>{b.items.map(([k, v], i) => (
        <tr key={i} className="border-b border-slate-100">
          <td className="py-1.5 pr-3 text-slate-500 w-1/3">{k}</td>
          <td className="py-1.5 font-semibold text-slate-800"><NEBadge v={v} /></td>
        </tr>))}</tbody>
    </table>);
  if (b.type === "table") return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse w-full">
        <thead className="bg-slate-50/60 text-[10px] uppercase tracking-widest text-slate-400">
          <tr>{b.headers.map((h) => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {b.rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j} className="px-2 py-1.5"><NEBadge v={c} /></td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>);
  if (b.type === "reference_list") return (
    <ol className="list-decimal list-inside space-y-1 text-xs">
      {b.items.map((r) => (
        <li key={r.index} className="text-slate-700 leading-relaxed">
          {r.reference} {r.doi && <span className="text-slate-400 font-mono-data">· doi:{r.doi}</span>}
        </li>
      ))}
    </ol>);
  return null;
}

function Meta({ r }) {
  const rev = r.reviewer_status || {};
  return (
    <div data-testid="v2-meta" className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-100 border border-slate-200 bg-white">
      {[
        ["Methodology", r.methodology_version],
        ["Data cutoff", r.data_cutoff_date],
        ["Generated", r.generation_date?.slice(0, 10)],
        ["Approvals", `${rev.approved_stage_count || 0} / ${rev.total_stages || 5}`],
        ["Approval status", APPROVAL_LABEL[r.approval_status] || r.approval_status],
      ].map(([l, v]) => (
        <div key={l} className="px-4 py-3">
          <p className="overline-label text-slate-400">{l}</p>
          {l === "Approval status" ? (
            <span className={`inline-block text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${APPROVAL_TONE[r.approval_status]}`}>{APPROVAL_LABEL[r.approval_status]}</span>
          ) : (
            <p className="font-heading font-bold text-sm mt-0.5"><NEBadge v={v || NE} /></p>
          )}
        </div>
      ))}
    </div>
  );
}

function Sections({ r }) {
  return (
    <div className="space-y-6">
      {r.sections.map((s) => (
        <section key={s.id} data-testid={`v2-section-${s.id}`} className="bg-white border border-slate-200 p-5">
          <div className="flex items-baseline justify-between border-b border-slate-200 pb-2 mb-3">
            <h3 className="font-heading font-bold text-sm">{s.title}</h3>
            {s.not_estimable && s.not_estimable.length > 0 && (
              <span data-testid={`v2-ne-${s.id}`} className="text-[9px] uppercase tracking-widest font-bold text-amber-700">
                {s.not_estimable.length} field(s) not estimable
              </span>
            )}
          </div>
          <div className="space-y-3">
            {s.blocks.map((b, i) => <Block key={i} b={b} />)}
          </div>
        </section>
      ))}
      {r.limitations && r.limitations !== NE && (
        <section data-testid="v2-limitations" className="bg-slate-50/60 border border-slate-200 p-4">
          <p className="overline-label text-slate-400">Limitations</p>
          <p className="text-xs text-slate-700 mt-1">{r.limitations}</p>
        </section>
      )}
    </div>
  );
}

export function ReportV2Panel() {
  const { productId } = useProduct();
  const { user } = useAuth();
  const [variant, setVariant] = useState("executive_summary");
  const [countryId, setCountryId] = useState("");
  const [countries, setCountries] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({ data_cutoff_date: "", limitations: "" });
  const [cfgSaving, setCfgSaving] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);

  const loadCountries = useCallback(() => {
    axios.get(`${API}/intelligence/${productId}`)
      .then((r) => setCountries(r.data?.countries || []))
      .catch(() => setCountries([]));
  }, [productId]);
  useEffect(() => { loadCountries(); }, [loadCountries]);

  useEffect(() => {
    axios.get(`${API}/report-v2/${productId}/config`).then((r) => {
      setCfg({ data_cutoff_date: r.data.data_cutoff_date || "",
                limitations: r.data.limitations || "" });
    }).catch(() => {});
  }, [productId]);

  const load = useCallback(async () => {
    setLoading(true); setData(null);
    try {
      let url = `${API}/report-v2/${productId}/executive`;
      if (variant === "technical_report") url = `${API}/report-v2/${productId}/technical`;
      if (variant === "country_annexure") {
        if (!countryId) { setLoading(false); return; }
        url = `${API}/report-v2/${productId}/annexure/${countryId}`;
      }
      const r = await axios.get(url);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Report generation failed"); }
    finally { setLoading(false); }
  }, [productId, variant, countryId]);

  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    setCfgSaving(true);
    try {
      await axios.put(`${API}/report-v2/${productId}/config`, cfg);
      toast.success("Report metadata saved");
      setCfgOpen(false); load();
    } catch { toast.error("Save failed"); }
    finally { setCfgSaving(false); }
  };

  return (
    <div data-testid="report-v2" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {[
          ["executive_summary", "Executive Strategy Summary", Globe],
          ["technical_report",  "Technical Report", Files],
          ["country_annexure",  "Country Annexure", MapPin],
        ].map(([k, l, Icon]) => (
          <button key={k} data-testid={`v2-variant-${k}`} onClick={() => setVariant(k)}
            className={`text-[11px] font-semibold px-3 py-2 border flex items-center gap-1.5 ${variant === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            <Icon size={11} /> {l}
          </button>
        ))}
        {variant === "country_annexure" && (
          <Select value={countryId || undefined} onValueChange={setCountryId}>
            <SelectTrigger data-testid="v2-country" className="h-9 w-56 text-xs bg-white"><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.flag} {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="v2-regen" onClick={load} disabled={loading}
            className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
            {loading ? <CircleNotch size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />} Regenerate
          </button>
          <button data-testid="v2-cfg" onClick={() => setCfgOpen(true)}
            className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-1.5 hover:bg-slate-50">
            Report settings
          </button>
        </div>
      </div>

      {variant === "country_annexure" && !countryId && (
        <div data-testid="v2-country-empty" className="py-16 text-center text-xs text-slate-400 border border-dashed border-slate-200">
          Select a country to generate an annexure.
        </div>
      )}

      {loading && (
        <div data-testid="v2-loading" className="py-16 text-center text-xs text-slate-400">
          Assembling report from live platform data…
        </div>
      )}

      {data && (
        <>
          <Meta r={data} />
          {data.not_estimable_summary?.length > 0 && (
            <div data-testid="v2-not-estimable-summary" className="bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2">
              <WarningCircle size={16} className="text-amber-700 mt-0.5" weight="fill" />
              <div>
                <p className="text-xs font-semibold text-amber-800">{data.not_estimable_summary.length} field(s) marked &ldquo;Not estimable&rdquo;</p>
                <p className="text-[11px] text-amber-700 mt-0.5">Missing values are never converted to zero. Populate them via the linked module to remove the flag.</p>
              </div>
            </div>
          )}
          <Sections r={data} />
          {data.appendix && (
            <section data-testid="v2-appendix" className="bg-white border border-slate-200 p-5">
              <h3 className="font-heading font-bold text-sm border-b border-slate-200 pb-2 mb-3">Appendix — Full inventories</h3>
              <p className="overline-label text-slate-400 mt-2">Evidence inventory ({data.appendix.evidence_inventory.length})</p>
              <ul className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                {data.appendix.evidence_inventory.map((e) => (
                  <li key={e.id}><b>{e.name || NE}</b> — {e.type} · {e.maturity || NE}</li>
                ))}
              </ul>
              <p className="overline-label text-slate-400 mt-3">Claims ledger ({data.appendix.claims_ledger.length})</p>
              <ul className="text-[11px] text-slate-600 mt-1 space-y-0.5">
                {data.appendix.claims_ledger.map((c) => (
                  <li key={c.id}>[{c.category}] {c.statement} — <span className="text-slate-400">{c.source_count} source(s)</span></li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {cfgOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCfgOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-200 max-w-md w-full p-5 space-y-3">
            <p className="font-heading font-bold text-sm">Report metadata</p>
            <div>
              <p className="overline-label text-slate-400">Data cutoff date</p>
              <Input data-testid="cfg-cutoff" className="h-8 text-xs mt-1" placeholder="YYYY-MM-DD"
                value={cfg.data_cutoff_date}
                onChange={(e) => setCfg({ ...cfg, data_cutoff_date: e.target.value })} />
            </div>
            <div>
              <p className="overline-label text-slate-400">Limitations</p>
              <Textarea data-testid="cfg-limitations" rows={4} className="text-xs mt-1"
                value={cfg.limitations}
                onChange={(e) => setCfg({ ...cfg, limitations: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setCfgOpen(false)} className="text-[11px] font-semibold text-slate-600 border border-slate-300 px-3 py-1.5 hover:bg-slate-50">Cancel</button>
              <button data-testid="cfg-save" onClick={saveCfg} disabled={cfgSaving}
                className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
                {cfgSaving ? <CircleNotch size={11} className="animate-spin" /> : <FloppyDisk size={11} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
